import {
  type GqlStripeCompanySubscriptionQuery,
  type GqlStripeUpcomingInvoice,
  useStripeCreateSubscriptionCheckoutSessionMutation,
  useStripePreviewSubUpdateMutation,
  useStripeUpgradeSubscriptionMutation,
} from '../../graphql/react-operations';
import { useStripe, Elements } from '@stripe/react-stripe-js';
import { SubPicker } from '../../components/stripe/SubPicker';
import { loadStripe } from '@stripe/stripe-js';
import { stripePublicKey } from '../../../environments/environment';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { ErrorBoundary } from '@startuptools/ui/ErrorBoundary';
import { GenericError } from '@startuptools/ui/Error';
import { BillingDocument } from './Billing.graphql';
import { Suspense, useState } from 'react';
import { isNil } from 'lodash-es';
import { activeSubStatuses } from '@startuptools/common/stripe';
import { Trans } from '@lingui/react';
import { AlertDialog } from '../../components/simple-dialogs/AlertDialog';
import { InvoiceDialog } from '../../components/stripe/InvoiceDialog';

const stripePromise = loadStripe(stripePublicKey);

const InnerSubscriptions = ({
  stripeCompanySubscription,
}: {
  stripeCompanySubscription: GqlStripeCompanySubscriptionQuery['stripeCompanySubscription'];
}) => {
  const companyId = useCompanyId();
  const stripe = useStripe();
  const [showInvalidSubDialog, setShowInvalidSubDialog] = useState(false);
  // eslint-disable-next-line sut/loading-mutation
  const [createCheckout] = useStripeCreateSubscriptionCheckoutSessionMutation({
    fetchPolicy: 'no-cache',
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doPreviewSubUpdate] = useStripePreviewSubUpdateMutation({ awaitRefetchQueries: true });
  // eslint-disable-next-line sut/loading-mutation
  const [doUpgradeSub] = useStripeUpgradeSubscriptionMutation({
    refetchQueries: [BillingDocument],
    awaitRefetchQueries: true,
  });
  const [invoice, setInvoice] = useState<GqlStripeUpcomingInvoice | null>(null);
  const [priceId, setPriceId] = useState<string | null>(null);

  if (!stripe) {
    return <GenericError />;
  }

  const handleSubPrice = async (priceId: string) => {
    if (!isNil(stripeCompanySubscription)) {
      if (!activeSubStatuses.includes(stripeCompanySubscription.status)) {
        // has a subscription but it's not valid (missing payments or whatever)
        setShowInvalidSubDialog(true);
        return false;
      }
      // has a valid subscripion, upgrade...
      const { data } = await doPreviewSubUpdate({ variables: { companyId, priceId } });
      if (!data) {
        throw new Error('missing invoice from preview request');
      }
      setPriceId(priceId);
      setInvoice(data.stripePreviewSubUpdate);
      return false;
    }
    // no subscription, create a new one
    const { data } = await createCheckout({
      variables: {
        companyId,
        input: { cancelUrl: window.location.href, successUrl: window.location.href, price: priceId },
      },
    });
    if (!data) {
      return false;
    }
    await stripe.redirectToCheckout({ sessionId: data.stripeCreateSubscriptionCheckoutSession.id });
    return true;
  };

  const handleUpgradeOnPaid = async () => {
    if (!priceId) {
      throw new Error('missing priceId, forgot to set it?');
    }
    await doUpgradeSub({ variables: { companyId, priceId } });
    setInvoice(null);
    setPriceId(null);
  };

  const handleCancel = () => {
    setInvoice(null);
    setPriceId(null);
  };

  return (
    <>
      <Suspense>
        <SubPicker onSubSelected={handleSubPrice} currentSubscription={stripeCompanySubscription} />
      </Suspense>
      {showInvalidSubDialog && (
        <AlertDialog
          title={<Trans id="Could not upgrade the subscription" />}
          onAccept={() => setShowInvalidSubDialog(false)}
          acceptButtonText="Ok"
        >
          <Trans id="Make sure all invoices are paid. If they are but you still can't upgrade, contact support by clicking the blue icon in the bottom right corner" />
        </AlertDialog>
      )}
      {invoice && (
        <InvoiceDialog invoice={invoice} companyId={companyId} onCancel={handleCancel} onPaid={handleUpgradeOnPaid} />
      )}
    </>
  );
};

export const Subscriptions = ({
  stripeCompanySubscription,
}: {
  stripeCompanySubscription: GqlStripeCompanySubscriptionQuery['stripeCompanySubscription'];
}) => {
  return (
    <Elements stripe={stripePromise}>
      <Suspense>
        <ErrorBoundary>
          <InnerSubscriptions stripeCompanySubscription={stripeCompanySubscription} />
        </ErrorBoundary>
      </Suspense>
    </Elements>
  );
};
